import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  AreaChart, Area, PieChart, Pie, Cell, BarChart, Bar,
  XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts'
import {
  ShieldCheck, RefreshCw, TrendingDown, TrendingUp,
  Newspaper, AlertTriangle, ExternalLink, Search,
  ArrowUp, ArrowDown, ChevronDown, ChevronUp, Zap,
  Activity, Layers, Target, Brain, CheckCircle, XCircle,
  TrendingUp as TUp, BarChart2, Info,
} from 'lucide-react'
import { healthAPI, isApiPaused } from '../services/api'
import { useThrottledTicks } from '../hooks/useThrottledTicks'
import AlertRulesPanel from '../components/AlertRulesPanel'
import AlertFeed from '../components/AlertFeed'
import OrderModal from '../components/OrderModal'
import LoadingSpinner from '../components/LoadingSpinner'
import { openTradingView } from '../utils/tradingView'
import toast from 'react-hot-toast'

// ─────────────────────────────────────────────────────────────────
// FORMATTERS
// ─────────────────────────────────────────────────────────────────
const fmt    = (n, d = 2) => n == null || isNaN(n) ? '—' : Number(n).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d })
const fmtL   = (n) => {
  if (n == null || isNaN(n)) return '—'
  const abs = Math.abs(n)
  const s   = n < 0 ? '−' : ''
  if (abs >= 1e7) return `${s}₹${(abs / 1e7).toFixed(2)} Cr`
  if (abs >= 1e5) return `${s}₹${(abs / 1e5).toFixed(2)} L`
  return `${s}₹${fmt(abs, 0)}`
}
const fmtPct = (n) => n == null ? '—' : `${n >= 0 ? '+' : ''}${fmt(n, 1)}%`
const clamp  = (v, lo = 0, hi = 100) => Math.max(lo, Math.min(hi, v ?? 0))

// ─────────────────────────────────────────────────────────────────
// DESIGN TOKENS
// ─────────────────────────────────────────────────────────────────
const ACCOUNT_CFG = {
  kite_pm:      { label: 'KPM', bg: 'bg-blue-100',    text: 'text-blue-700',   color: '#3b82f6' },
  kite_pm_live: { label: 'KPM', bg: 'bg-blue-100',    text: 'text-blue-700',   color: '#3b82f6' },
  kite_rj:      { label: 'KRJ', bg: 'bg-sky-100',     text: 'text-sky-700',    color: '#0ea5e9' },
  angelone_rj:  { label: 'ARJ', bg: 'bg-orange-100',  text: 'text-orange-700', color: '#f97316' },
  angelone_pm:  { label: 'APM', bg: 'bg-violet-100',  text: 'text-violet-700', color: '#8b5cf6' },
}

const CHART_PALETTE = ['#6366f1','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#f97316','#84cc16','#64748b','#ec4899','#14b8a6','#a855f7']

const hlevel = (s) => {
  const v = Math.round(s ?? 0)
  if (v >= 80) return { label: 'Strong',   bg: 'bg-emerald-50',  text: 'text-emerald-700', bar: 'bg-emerald-500', border: 'border-emerald-200', ring: '#10b981', tile: 'bg-emerald-100 border-emerald-300' }
  if (v >= 65) return { label: 'Good',     bg: 'bg-green-50',    text: 'text-green-700',   bar: 'bg-green-400',   border: 'border-green-200',   ring: '#4ade80', tile: 'bg-green-100 border-green-300'   }
  if (v >= 50) return { label: 'Watch',    bg: 'bg-amber-50',    text: 'text-amber-700',   bar: 'bg-amber-400',   border: 'border-amber-200',   ring: '#fbbf24', tile: 'bg-amber-100 border-amber-300'   }
  if (v >= 35) return { label: 'Weak',     bg: 'bg-orange-50',   text: 'text-orange-700',  bar: 'bg-orange-400',  border: 'border-orange-200',  ring: '#fb923c', tile: 'bg-orange-100 border-orange-300' }
  return              { label: 'Critical', bg: 'bg-red-50',      text: 'text-red-700',     bar: 'bg-red-500',     border: 'border-red-200',     ring: '#ef4444', tile: 'bg-red-100 border-red-300'       }
}

const dedupeAccounts = (accounts = []) => {
  const seen = new Set()
  return accounts.filter(a => {
    const key = ACCOUNT_CFG[a]?.label || a
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

// ─────────────────────────────────────────────────────────────────
// HEALTH GAUGE (SVG ring)
// ─────────────────────────────────────────────────────────────────
const HealthGauge = ({ score = 0, size = 'lg' }) => {
  const s    = clamp(score)
  const hl   = hlevel(s)
  const dim  = size === 'sm' ? 80 : 128
  const r    = size === 'sm' ? 30 : 50
  const sw   = size === 'sm' ? 8  : 12
  const circ = 2 * Math.PI * r
  const offset = circ - (s / 100) * circ
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative" style={{ width: dim, height: dim }}>
        <svg style={{ width: dim, height: dim }} className="-rotate-90" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r={r} fill="none" stroke="#f1f5f9" strokeWidth={sw} />
          <circle cx="60" cy="60" r={r} fill="none" strokeWidth={sw} strokeLinecap="round"
            stroke={hl.ring}
            strokeDasharray={circ}
            strokeDashoffset={offset}
            style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(.4,0,.2,1)' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center select-none">
          <span className={`font-black tabular-nums leading-none ${size === 'sm' ? 'text-xl' : 'text-4xl'} ${hl.text}`}>
            {Math.round(s)}
          </span>
          {size !== 'sm' && <span className="text-[9px] text-gray-400 font-semibold tracking-widest mt-0.5">/100</span>}
        </div>
      </div>
      <span className={`text-xs font-bold px-3 py-0.5 rounded-full border ${hl.bg} ${hl.text} ${hl.border}`}>
        {hl.label}
      </span>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// PORTFOLIO EQUITY CURVE
// ─────────────────────────────────────────────────────────────────
const RANGES = [
  { label: '1M', days: 30 },
  { label: '3M', days: 90 },
  { label: '6M', days: 180 },
  { label: '1Y', days: 365 },
  { label: 'ALL', days: Infinity },
]

const EquityChart = ({ curve = [], peakValue = 0, peakDate }) => {
  const [range, setRange] = useState('ALL')

  const filtered = useMemo(() => {
    const r = RANGES.find(x => x.label === range)
    if (!r || r.days === Infinity) return curve
    const cutoff = new Date(Date.now() - r.days * 86400000).toISOString().slice(0, 10)
    return curve.filter(d => d.date >= cutoff)
  }, [curve, range])

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.[0]) return null
    const val = payload[0].value
    return (
      <div className="bg-gray-900 text-white px-3 py-2 rounded-lg shadow-xl text-xs">
        <p className="text-gray-400 mb-0.5">{label}</p>
        <p className="font-bold text-white">{fmtL(val)}</p>
        {peakValue > 0 && val < peakValue && (
          <p className="text-red-400 mt-0.5">−{fmt((peakValue - val) / peakValue * 100, 1)}% from peak</p>
        )}
      </div>
    )
  }

  if (!filtered.length) return (
    <div className="h-48 flex flex-col items-center justify-center text-gray-300">
      <Activity className="w-10 h-10 mb-2 opacity-40" />
      <p className="text-sm font-medium text-gray-400">No history yet</p>
      <p className="text-xs text-gray-300 mt-1">Portfolio value will chart here after daily snapshots accumulate</p>
    </div>
  )

  const minVal = Math.min(...filtered.map(d => d.value)) * 0.985
  const maxVal = Math.max(...filtered.map(d => d.value)) * 1.01

  return (
    <div>
      {/* Range selector */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Portfolio Value</p>
        <div className="flex gap-0.5 bg-gray-100 p-0.5 rounded-lg">
          {RANGES.map(r => (
            <button key={r.label} onClick={() => setRange(r.label)}
              className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${
                range === r.label ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={filtered} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="gradPortfolio" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor="#6366f1" stopOpacity={0.25} />
              <stop offset="95%" stopColor="#6366f1" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false}
            axisLine={false}
            tickFormatter={d => { const [,m,day] = d.split('-'); return `${day}/${m}` }}
            interval={Math.max(1, Math.floor(filtered.length / 6))}
          />
          <YAxis domain={[minVal, maxVal]} tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false}
            axisLine={false} tickFormatter={v => fmtL(v)} width={72}
          />
          <Tooltip content={<CustomTooltip />} />
          {peakDate && peakValue > 0 && (
            <ReferenceLine x={peakDate} stroke="#ef4444" strokeDasharray="4 2" strokeWidth={1.5}
              label={{ value: 'Peak', position: 'insideTopRight', fontSize: 9, fill: '#ef4444' }}
            />
          )}
          <Area type="monotone" dataKey="value"
            stroke="#6366f1" strokeWidth={2.5}
            fill="url(#gradPortfolio)"
            dot={false} activeDot={{ r: 5, fill: '#6366f1', strokeWidth: 2, stroke: '#fff' }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// ALLOCATION DONUT
// ─────────────────────────────────────────────────────────────────
const AllocationDonut = ({ holdings = [] }) => {
  const [hovered, setHovered] = useState(null)

  const sorted = [...holdings].sort((a, b) => (b.portfolioPct || 0) - (a.portfolioPct || 0))
  const top9   = sorted.slice(0, 9)
  const rest   = sorted.slice(9).reduce((s, h) => s + (h.portfolioPct || 0), 0)
  const data   = [
    ...top9.map(h => ({ name: h.symbol, value: +(h.portfolioPct || 0).toFixed(1), health: h.healthScore })),
    ...(rest > 0.1 ? [{ name: 'Others', value: +rest.toFixed(1), health: 60 }] : []),
  ]

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.[0]) return null
    const d = payload[0].payload
    return (
      <div className="bg-gray-900 text-white px-3 py-2 rounded-lg text-xs shadow-xl">
        <p className="font-bold">{d.name}</p>
        <p className="text-gray-300">{d.value}% of portfolio</p>
        {d.name !== 'Others' && <p className={`mt-0.5 ${hlevel(d.health || 0).text.replace('text-', 'text-')}`}>Health {Math.round(d.health || 0)}</p>}
      </div>
    )
  }

  if (!data.length) return (
    <div className="h-48 flex items-center justify-center text-gray-300 text-sm">No allocation data</div>
  )

  return (
    <div className="flex items-center gap-4">
      {/* Donut */}
      <div className="flex-shrink-0">
        <ResponsiveContainer width={160} height={160}>
          <PieChart>
            <Pie data={data} cx={75} cy={75} innerRadius={42} outerRadius={72}
              dataKey="value" strokeWidth={2} stroke="#fff"
              onMouseEnter={(_, i) => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]}
                  opacity={hovered == null || hovered === i ? 1 : 0.5}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      {/* Legend */}
      <div className="flex-1 space-y-1.5 min-w-0">
        {data.map((d, i) => (
          <div key={d.name} className="flex items-center gap-2"
            onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}>
            <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
              style={{ backgroundColor: CHART_PALETTE[i % CHART_PALETTE.length] }} />
            <span className="text-xs text-gray-700 font-medium truncate flex-1">{d.name}</span>
            <span className="text-xs tabular-nums text-gray-500 flex-shrink-0">{d.value}%</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// POSITION HEAT MAP (Bloomberg-style)
// ─────────────────────────────────────────────────────────────────
const PositionHeatMap = ({ holdings = [] }) => {
  const sorted = [...holdings].sort((a, b) => (b.portfolioPct || 0) - (a.portfolioPct || 0))
  if (!sorted.length) return null

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Position Map</p>
        <div className="flex items-center gap-3 text-[10px] text-gray-400">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-emerald-200 rounded-sm inline-block border border-emerald-300" /> Strong (80+)</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-amber-200 rounded-sm inline-block border border-amber-300" /> Watch (50–64)</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-red-200 rounded-sm inline-block border border-red-300" /> Critical (&lt;35)</span>
          <span className="text-gray-300">· size = portfolio weight</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 p-3 bg-gray-50 rounded-xl border border-gray-100">
        {sorted.map(h => {
          const hl   = hlevel(h.healthScore || 0)
          const pct  = h.portfolioPct || 0
          // Width scales from 56px (0%) to 160px (20%+)
          const w    = Math.round(56 + Math.min(pct / 20, 1) * 104)
          const tall = pct > 8
          const pnlPos = (h.pnlPct || 0) >= 0
          return (
            <button key={h.symbol}
              onClick={() => openTradingView(h.symbol, h.exchange)}
              title={`${h.symbol}: ${pct.toFixed(1)}% | Health ${Math.round(h.healthScore||0)} | P&L ${h.pnlPct?.toFixed(1)}%`}
              className={`rounded-lg border text-left transition-all hover:scale-105 hover:shadow-md ${hl.tile}`}
              style={{ width: w, padding: tall ? '10px 8px' : '6px 8px' }}
            >
              <div className={`font-black text-[11px] leading-none ${hl.text} truncate`}>{h.symbol}</div>
              <div className={`text-[9px] mt-0.5 font-semibold ${pnlPos ? 'text-emerald-600' : 'text-red-500'}`}>
                {pnlPos ? '+' : ''}{fmt(h.pnlPct, 1)}%
              </div>
              {tall && (
                <div className="text-[9px] text-gray-400 mt-0.5">{pct.toFixed(1)}% book</div>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// SCORE BREAKDOWN BARS
// ─────────────────────────────────────────────────────────────────
const ScoreBar = ({ label, value, weight, dimmed = false, desc }) => {
  const v  = Math.round(value ?? 0)
  const hl = hlevel(v)
  return (
    <div className={`group ${dimmed ? 'opacity-45' : ''}`}>
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs text-gray-600 flex items-center gap-1">
          {label}
          <span className="text-[10px] text-gray-400">({weight})</span>
        </span>
        <span className={`text-sm font-black tabular-nums ${hl.text}`}>{v}</span>
      </div>
      <div className="relative h-2.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`absolute inset-y-0 left-0 rounded-full transition-all duration-700 ${hl.bar}`}
          style={{ width: `${clamp(value)}%` }} />
      </div>
      {desc && <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">{desc}</p>}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// ACCOUNT CONTRIBUTION CHART
// ─────────────────────────────────────────────────────────────────
const AccountChart = ({ accountBreakdown = [] }) => {
  // Use per-account breakdown from backend (ExternalHoldings docs read directly).
  // This is the single correct source — the aggregated per-symbol holdings
  // cannot give accurate per-account splits without knowing each account's qty.
  const acctTotals = useMemo(() => {
    return (accountBreakdown || [])
      .filter(a => (a.currentValue || 0) > 0)
      .map(a => ({
        name:  a.label || a.accountName,
        value: Math.round(a.currentValue || 0),
        color: Object.values(ACCOUNT_CFG).find(c => c.label === a.label)?.color || '#64748b',
      }))
  }, [accountBreakdown])

  if (!acctTotals.length) return null

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.[0]) return null
    return (
      <div className="bg-gray-900 text-white px-3 py-2 rounded-lg text-xs shadow-xl">
        <p className="font-bold">{payload[0].payload.name}</p>
        <p className="text-gray-300">{fmtL(payload[0].value)}</p>
      </div>
    )
  }

  return (
    <div>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">By Account</p>
      <ResponsiveContainer width="100%" height={100}>
        <BarChart data={acctTotals} layout="vertical" margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} width={30} />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={18}>
            {acctTotals.map((d, i) => <Cell key={i} fill={d.color} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// SORT HEADER
// ─────────────────────────────────────────────────────────────────
const SortTh = ({ field, label, right = false, sortKey, sortDir, onSort }) => {
  const active = sortKey === field
  return (
    <th onClick={() => onSort(field)}
      className={`px-3 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide cursor-pointer select-none hover:bg-gray-100 transition-colors ${right ? 'text-right' : 'text-left'}`}>
      <span className="inline-flex items-center gap-0.5">
        {label}
        {active
          ? (sortDir === 'asc' ? <ArrowUp className="w-3 h-3 text-indigo-500" /> : <ArrowDown className="w-3 h-3 text-indigo-500" />)
          : <ArrowUp className="w-3 h-3 text-gray-200" />}
      </span>
    </th>
  )
}

// ─────────────────────────────────────────────────────────────────
// MAIN HEALTH PAGE
// ─────────────────────────────────────────────────────────────────
const Health = () => {
  // Throttled to 5 s — prevents table sort flickering / row-shuffle on every WS tick
  const symbolTicks = useThrottledTicks(5000)

  const [data,           setData]           = useState(null)
  const [pulse,          setPulse]          = useState({ held: [], general: [], total: 0 })
  const [loading,        setLoading]        = useState(true)
  const [refreshing,     setRefreshing]     = useState(false)
  const [refreshingNews, setRefreshingNews] = useState(false)
  const [feedKey,        setFeedKey]        = useState(0)
  const [orderModal,     setOrderModal]     = useState({ isOpen: false, stock: null, suggested: null })
  const [sortKey,        setSortKey]        = useState('healthScore')
  const [sortDir,        setSortDir]        = useState('asc')
  const [search,         setSearch]         = useState('')
  const [newsOpen,       setNewsOpen]       = useState(false)
  const [view,           setView]           = useState('table')   // 'table' | 'heatmap'
  const [autoNews,       setAutoNews]       = useState(() => {
    try { return localStorage.getItem('health_auto_refresh_news') !== 'false' } catch { return true }
  })
  // Ranked analysis state
  const [analysis,        setAnalysis]       = useState(null)
  const [analysisLoading, setAnalysisLoading]= useState(false)
  const [analysisOpen,    setAnalysisOpen]   = useState(true)
  const [expandedRanks,   setExpandedRanks]  = useState({}) // symbol → bool

  // ── Load ────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    try {
      const [sumRes, pulseRes] = await Promise.allSettled([
        healthAPI.getSummary(),
        healthAPI.getPulse(),
      ])
      if (sumRes.status   === 'fulfilled') setData(sumRes.value.data)
      if (pulseRes.status === 'fulfilled') setPulse(pulseRes.value.data)
    } catch { toast.error('Failed to load health data') }
    finally { setLoading(false) }
  }, [])

  const loadAnalysis = useCallback(async (force = false) => {
    setAnalysisLoading(true)
    try {
      const res = await healthAPI.getAnalysis(force)
      setAnalysis(res.data)
    } catch { /* non-fatal */ }
    finally { setAnalysisLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])
  useEffect(() => { loadAnalysis() }, [loadAnalysis])

  const refreshNews = useCallback(async (silent = false) => {
    setRefreshingNews(true)
    try {
      await healthAPI.refreshNews()
      const p = await healthAPI.getPulse()
      setPulse(p.data)
      if (!silent) toast.success('News refreshed')
    } catch { if (!silent) toast.error('News refresh failed') }
    finally { setRefreshingNews(false) }
  }, [])

  useEffect(() => {
    try { localStorage.setItem('health_auto_refresh_news', String(autoNews)) } catch {}
    if (!autoNews) return
    const id = setInterval(() => { if (!isApiPaused()) refreshNews(true) }, 5 * 60 * 1000)
    return () => clearInterval(id)
  }, [autoNews, refreshNews])

  const refresh = async () => {
    setRefreshing(true)
    try {
      const [snapRes] = await Promise.allSettled([
        healthAPI.refresh(),
        loadAnalysis(true),   // force-refresh analysis cache too
      ])
      if (snapRes.status === 'fulfilled') setData(snapRes.value.data)
      setFeedKey(k => k + 1)
      toast.success('Health refreshed')
    } catch { toast.error('Refresh failed') }
    finally { setRefreshing(false) }
  }

  // ── Sort / filter ────────────────────────────────────────────────
  const handleSort = (field) => {
    if (sortKey === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(field); setSortDir('asc') }
  }

  // Overlay WebSocket LTP and recompute P&L
  const withLive = (h) => {
    const ltp      = symbolTicks?.[h.symbol]?.ltp || h.ltp || 0
    const val      = ltp > 0 ? ltp * h.totalQuantity : h.totalValue
    const pnl      = val - (h.totalInvested || 0)
    const pnlPct   = (h.totalInvested || 0) > 0 ? (pnl / h.totalInvested) * 100 : (h.pnlPct || 0)
    const portfolioPct = (data?.portfolio?.totalValue || 0) > 0
      ? (val / data.portfolio.totalValue) * 100
      : (h.portfolioPct || 0)
    return { ...h, ltp, currentValue: val, pnl, pnlPct, portfolioPct, isLive: !!symbolTicks?.[h.symbol] }
  }

  const processedHoldings = useMemo(() => {
    return (data?.holdings || []).map(withLive)
  }, [data, symbolTicks])  // eslint-disable-line

  const sortedHoldings = useMemo(() => {
    const q   = search.toLowerCase().trim()
    const fil = q ? processedHoldings.filter(h => h.symbol.toLowerCase().includes(q)) : processedHoldings
    return [...fil].sort((a, b) => {
      let va = a[sortKey] ?? 0, vb = b[sortKey] ?? 0
      if (typeof va === 'string') { va = va.toLowerCase(); vb = vb.toLowerCase() }
      return sortDir === 'asc' ? (va > vb ? 1 : va < vb ? -1 : 0) : (va < vb ? 1 : va > vb ? -1 : 0)
    })
  }, [processedHoldings, search, sortKey, sortDir])

  const reviewOrder = (suggested) => setOrderModal({
    isOpen: true,
    stock: { symbol: suggested.tradingsymbol, tradingsymbol: suggested.tradingsymbol, exchange: suggested.exchange || 'NSE' },
    suggested, eventId: null,
  })

  if (loading) return <div className="flex justify-center py-24"><LoadingSpinner size="large" /></div>

  const p        = data?.portfolio || {}
  const curve    = data?.drawdownCurve || []
  const holdings = sortedHoldings
  const pnlPos   = (p.totalPnl || 0) >= 0
  const retPct   = (p.totalInvested || 0) > 0 ? (p.totalPnl || 0) / p.totalInvested * 100 : 0
  const liveCount = processedHoldings.filter(h => h.isLive).length

  const newsBySymbol = {}
  for (const item of pulse.held || []) {
    if (item.items?.[0]?.url) newsBySymbol[item.symbol] = item.items[0]
  }

  // ── RENDER ─────────────────────────────────────────────────────
  return (
    <div className="space-y-4">

      {/* ════════════════════════════════════════════════════════
          HERO BANNER
      ════════════════════════════════════════════════════════ */}
      <div className="bg-gradient-to-r from-indigo-600 via-indigo-700 to-violet-700 rounded-2xl p-5 text-white shadow-lg shadow-indigo-200">
        <div className="flex flex-wrap items-start justify-between gap-4">
          {/* Left — portfolio value */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="w-4 h-4 text-indigo-300" />
              <span className="text-xs font-semibold text-indigo-200 uppercase tracking-widest">Portfolio Health</span>
              {data?.date && <span className="text-[10px] text-indigo-300">{data.date}</span>}
            </div>
            <div className="text-4xl font-black tabular-nums leading-none">{fmtL(p.totalValue)}</div>
            <div className="flex items-center gap-3 mt-2">
              <span className={`text-base font-bold ${pnlPos ? 'text-emerald-300' : 'text-red-300'}`}>
                {pnlPos ? '+' : ''}{fmtL(p.totalPnl)}
              </span>
              <span className={`text-sm font-semibold px-2 py-0.5 rounded-full ${pnlPos ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>
                {fmtPct(retPct)}
              </span>
              {liveCount > 0 && (
                <span className="flex items-center gap-1 text-xs text-indigo-300">
                  <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                  {liveCount} live
                </span>
              )}
            </div>
            <div className="mt-2 text-xs text-indigo-300">
              Invested {fmtL(p.totalInvested)} · {processedHoldings.length} stocks ·{' '}
              {[...new Set(processedHoldings.flatMap(h => dedupeAccounts(h.accounts).map(a => ACCOUNT_CFG[a]?.label || a)))].join(', ')}
            </div>
          </div>

          {/* Right — score gauge */}
          <div className="flex items-center gap-6">
            <div className="text-center">
              <HealthGauge score={p.healthScore} />
            </div>
            <button onClick={refresh} disabled={refreshing}
              className="flex items-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold rounded-lg transition-colors border border-white/20">
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════
          EQUITY CURVE
      ════════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <EquityChart curve={curve} peakValue={p.peakValue} peakDate={p.peakDate} />
      </div>

      {/* ════════════════════════════════════════════════════════
          KPI ROW
      ════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">

        {/* Drawdown */}
        <div className={`bg-white rounded-2xl border shadow-sm p-4 ${(p.drawdownPct||0) > 10 ? 'border-red-200' : 'border-gray-100'}`}>
          <div className="flex items-center gap-1.5 mb-2">
            <TrendingDown className={`w-4 h-4 ${(p.drawdownPct||0) > 10 ? 'text-red-500' : 'text-gray-400'}`} />
            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Drawdown</span>
          </div>
          <div className={`text-2xl font-black tabular-nums ${(p.drawdownPct||0) > 10 ? 'text-red-600' : (p.drawdownPct||0) > 5 ? 'text-amber-600' : 'text-emerald-600'}`}>
            −{fmt(p.drawdownPct, 1)}%
          </div>
          <div className="mt-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${(p.drawdownPct||0) > 10 ? 'bg-red-400' : (p.drawdownPct||0) > 5 ? 'bg-amber-400' : 'bg-emerald-400'}`}
              style={{ width: `${Math.min((p.drawdownPct||0) * 4, 100)}%` }} />
          </div>
          <p className="text-[10px] text-gray-400 mt-1">from peak {p.peakDate && `(${p.peakDate})`}</p>
        </div>

        {/* Concentration */}
        <div className={`bg-white rounded-2xl border shadow-sm p-4 ${(p.maxSingleStockPct||0) > 12 ? 'border-red-200' : 'border-gray-100'}`}>
          <div className="flex items-center gap-1.5 mb-2">
            <Target className={`w-4 h-4 ${(p.maxSingleStockPct||0) > 12 ? 'text-red-500' : 'text-gray-400'}`} />
            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Max Stock</span>
          </div>
          <div className={`text-2xl font-black tabular-nums flex items-center gap-1.5 ${(p.maxSingleStockPct||0) > 12 ? 'text-red-600' : 'text-gray-800'}`}>
            {fmt(p.maxSingleStockPct, 1)}%
            {(p.maxSingleStockPct||0) > 12 && <AlertTriangle className="w-4 h-4 text-red-500" />}
          </div>
          <div className="mt-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${(p.maxSingleStockPct||0) > 12 ? 'bg-red-400' : 'bg-indigo-400'}`}
              style={{ width: `${Math.min((p.maxSingleStockPct||0) / 30 * 100, 100)}%` }} />
          </div>
          <p className="text-[10px] text-gray-400 mt-1">limit 12% · top 5 = {fmt(p.topNConcentration, 1)}%</p>
        </div>

        {/* Diversification */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <div className="flex items-center gap-1.5 mb-2">
            <Layers className="w-4 h-4 text-gray-400" />
            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Diversification</span>
          </div>
          <div className="text-2xl font-black tabular-nums text-gray-800">{processedHoldings.length}</div>
          <div className="mt-1 flex gap-1 flex-wrap">
            {[...new Set(processedHoldings.flatMap(h => dedupeAccounts(h.accounts).map(a => ACCOUNT_CFG[a]?.label || a)))].map(lbl => {
              const cfg = Object.values(ACCOUNT_CFG).find(c => c.label === lbl) || {}
              return (
                <span key={lbl} className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${cfg.bg || 'bg-gray-100'} ${cfg.text || 'text-gray-600'}`}>{lbl}</span>
              )
            })}
          </div>
          <p className="text-[10px] text-gray-400 mt-1">HHI {fmt((p.herfindahl||0)*100, 1)}</p>
        </div>

        {/* Score */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <div className="flex items-center gap-1.5 mb-2">
            <Zap className="w-4 h-4 text-gray-400" />
            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Score Detail</span>
          </div>
          <div className="space-y-1.5 mt-1">
            {[
              { k: 'priceAction',   label: 'Price', w: '40%' },
              { k: 'drawdown',      label: 'DD',    w: '30%' },
              { k: 'concentration', label: 'Conc',  w: '30%' },
            ].map(({ k, label, w }) => {
              const v  = Math.round(p.scoreBreakdown?.[k] ?? 0)
              const hl = hlevel(v)
              return (
                <div key={k} className="flex items-center gap-1.5">
                  <span className="text-[10px] text-gray-400 w-8">{label}</span>
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${hl.bar}`} style={{ width: `${v}%` }} />
                  </div>
                  <span className={`text-[10px] font-bold w-6 text-right tabular-nums ${hl.text}`}>{v}</span>
                  <span className="text-[9px] text-gray-300 w-7">{w}</span>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════
          ALLOCATION + ACCOUNT CHART
      ════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        {/* Allocation donut */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Allocation</p>
          <AllocationDonut holdings={processedHoldings} />
        </div>

        {/* Account chart + score breakdown */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-5">
          <AccountChart accountBreakdown={data?.accountBreakdown} />
          <div className="space-y-3 border-t border-gray-50 pt-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Health Breakdown</p>
            <ScoreBar label="Price Action"   value={p.scoreBreakdown?.priceAction}   weight="40%" desc="Weighted avg P&L across all holdings" />
            <ScoreBar label="Drawdown"       value={p.scoreBreakdown?.drawdown}      weight="30%" desc="Decline from all-time peak portfolio value" />
            <ScoreBar label="Concentration"  value={p.scoreBreakdown?.concentration} weight="30%" desc="Largest single stock vs 12% threshold" />
            <ScoreBar label="News Sentiment" value={p.scoreBreakdown?.news}          weight="—"   dimmed desc="Computed · not weighted yet" />
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════
          POSITION HEAT MAP + HOLDINGS TABLE (toggle)
      ════════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

        {/* Section header */}
        <div className="px-5 py-3.5 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-3">
              <h3 className="font-semibold text-gray-900">
                Holdings
                <span className="text-sm font-normal text-gray-400 ml-1.5">
                  ({processedHoldings.length} symbols · aggregated across all accounts)
                </span>
              </h3>
              {/* Table / HeatMap toggle */}
              <div className="flex gap-0.5 bg-gray-100 p-0.5 rounded-lg">
                <button onClick={() => setView('table')}
                  className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${view === 'table' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                  Table
                </button>
                <button onClick={() => setView('heatmap')}
                  className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${view === 'heatmap' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                  Map
                </button>
              </div>
            </div>
            <p className="text-[11px] text-gray-400 mt-0.5">
              Quantities summed · price weighted across accounts · worst health first
            </p>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Filter symbol…"
              className="border border-gray-200 rounded-lg pl-8 pr-3 py-1.5 text-sm w-36 focus:outline-none focus:ring-2 focus:ring-indigo-300" />
          </div>
        </div>

        {/* ── HEAT MAP view ── */}
        {view === 'heatmap' && (
          <div className="p-5">
            <PositionHeatMap holdings={processedHoldings} />
          </div>
        )}

        {/* ── TABLE view ── */}
        {view === 'table' && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[860px]">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <SortTh field="symbol"        label="Symbol"   sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                    <th className="px-3 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide text-left">Accounts</th>
                    <SortTh field="totalQuantity" label="Qty"      sortKey={sortKey} sortDir={sortDir} onSort={handleSort} right />
                    <SortTh field="avgPrice"      label="Avg ₹"    sortKey={sortKey} sortDir={sortDir} onSort={handleSort} right />
                    <SortTh field="ltp"           label="LTP ₹"    sortKey={sortKey} sortDir={sortDir} onSort={handleSort} right />
                    <SortTh field="currentValue"  label="Value"    sortKey={sortKey} sortDir={sortDir} onSort={handleSort} right />
                    <SortTh field="portfolioPct"  label="% Book"   sortKey={sortKey} sortDir={sortDir} onSort={handleSort} right />
                    <SortTh field="pnlPct"        label="P&L"      sortKey={sortKey} sortDir={sortDir} onSort={handleSort} right />
                    <SortTh field="healthScore"   label="Health"   sortKey={sortKey} sortDir={sortDir} onSort={handleSort} right />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {holdings.length === 0 && (
                    <tr><td colSpan={9} className="py-14 text-center">
                      <Activity className="w-10 h-10 mx-auto mb-3 text-gray-200" />
                      <p className="text-gray-400 font-medium">No holdings found</p>
                      <p className="text-xs text-gray-300 mt-1">Connect Kite or upload holdings in Portfolio</p>
                    </td></tr>
                  )}
                  {holdings.map(h => {
                    const hl       = hlevel(h.healthScore || 0)
                    const pnlPos   = (h.pnlPct || 0) >= 0
                    const overConc = (h.portfolioPct || 0) > 12 && h.instrumentType !== 'ETF'
                    const accts    = dedupeAccounts(h.accounts || [])
                    return (
                      <tr key={h.symbol}
                        className={`transition-colors hover:bg-indigo-50/30 ${overConc ? 'bg-red-50/40' : ''}`}>

                        {/* Symbol */}
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1.5">
                            <button onClick={() => openTradingView(h.symbol, h.exchange)}
                              className="font-bold text-indigo-700 hover:text-indigo-900 tracking-wide text-sm">
                              {h.symbol}
                            </button>
                            {h.isLive && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" title="Live stream" />}
                            {newsBySymbol[h.symbol] && (
                              <a href={newsBySymbol[h.symbol].url} target="_blank" rel="noreferrer"
                                title={newsBySymbol[h.symbol].title}
                                className="text-amber-400 hover:text-amber-600">
                                <Newspaper className="w-3.5 h-3.5" />
                              </a>
                            )}
                          </div>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${h.instrumentType === 'ETF' ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                            {h.instrumentType || 'Stock'}
                          </span>
                        </td>

                        {/* Account badges */}
                        <td className="px-3 py-3">
                          <div className="flex flex-wrap gap-1">
                            {accts.map(a => {
                              const cfg = ACCOUNT_CFG[a] || { label: a.slice(0, 3).toUpperCase(), bg: 'bg-gray-100', text: 'text-gray-600' }
                              return <span key={a} className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${cfg.bg} ${cfg.text}`}>{cfg.label}</span>
                            })}
                          </div>
                        </td>

                        {/* Quantity */}
                        <td className="px-3 py-3 text-right tabular-nums text-gray-700">{fmt(h.totalQuantity, 0)}</td>

                        {/* Weighted avg price */}
                        <td className="px-3 py-3 text-right tabular-nums text-gray-500 text-xs">
                          {h.avgPrice > 0 ? `₹${fmt(h.avgPrice)}` : '—'}
                        </td>

                        {/* LTP */}
                        <td className="px-3 py-3 text-right tabular-nums font-semibold text-gray-800">
                          {h.ltp > 0 ? `₹${fmt(h.ltp)}` : '—'}
                        </td>

                        {/* Value */}
                        <td className="px-3 py-3 text-right tabular-nums font-bold text-gray-900">
                          {fmtL(h.currentValue)}
                        </td>

                        {/* % Book — red if overweight */}
                        <td className={`px-3 py-3 text-right tabular-nums font-medium text-xs ${overConc ? 'text-red-600' : 'text-gray-500'}`}>
                          {fmt(h.portfolioPct, 1)}%
                          {overConc && <AlertTriangle className="inline w-3 h-3 ml-0.5 text-red-400" />}
                        </td>

                        {/* P&L (% + ₹) */}
                        <td className="px-3 py-3 text-right">
                          <div className={`font-bold tabular-nums text-sm ${pnlPos ? 'text-emerald-600' : 'text-red-600'}`}>
                            {pnlPos ? '+' : ''}{fmt(h.pnlPct, 1)}%
                          </div>
                          {h.pnl != null && (
                            <div className={`text-[10px] tabular-nums ${pnlPos ? 'text-emerald-400' : 'text-red-400'}`}>
                              {pnlPos ? '+' : ''}{fmtL(h.pnl)}
                            </div>
                          )}
                        </td>

                        {/* Health badge */}
                        <td className="px-3 py-3 text-right">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold border ${hl.bg} ${hl.text} ${hl.border}`}>
                            <span className="tabular-nums">{Math.round(h.healthScore || 0)}</span>
                            <span className="font-normal opacity-70 hidden sm:inline">{hl.label}</span>
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Portfolio weight bar at table bottom */}
            {processedHoldings.length > 0 && (
              <div className="px-5 py-3 border-t border-gray-50 bg-gray-50/60">
                <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-1.5">Weight distribution</p>
                <div className="flex h-2.5 rounded-full overflow-hidden gap-px">
                  {[...processedHoldings]
                    .sort((a, b) => (b.portfolioPct||0) - (a.portfolioPct||0))
                    .filter(h => (h.portfolioPct||0) > 0)
                    .slice(0, 15)
                    .map((h, i) => (
                      <div key={h.symbol} title={`${h.symbol}: ${fmt(h.portfolioPct, 1)}%`}
                        className="rounded-sm"
                        style={{ width: `${clamp(h.portfolioPct)}%`, backgroundColor: CHART_PALETTE[i % CHART_PALETTE.length] }}
                      />
                    ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ════════════════════════════════════════════════════════
          RANKED ANALYSIS — 5-factor model with reasoning
      ════════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <button onClick={() => setAnalysisOpen(v => !v)}
          className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-3">
            <Brain className="w-5 h-5 text-indigo-500" />
            <div className="text-left">
              <h3 className="font-bold text-gray-900">Ranked Analysis</h3>
              <p className="text-xs text-gray-400 mt-0.5">
                5-factor model · return quality · momentum · positioning · drawdown · news sentiment
              </p>
            </div>
            {analysis && (
              <div className="flex gap-2 ml-2">
                {['CRITICAL','VERY HIGH','HIGH'].map(lvl => {
                  const n = analysis.rankings?.filter(r => r.riskLevel === lvl).length || 0
                  if (!n) return null
                  const col = lvl === 'CRITICAL' ? 'bg-red-100 text-red-700' : lvl === 'VERY HIGH' ? 'bg-orange-100 text-orange-700' : 'bg-amber-100 text-amber-700'
                  return <span key={lvl} className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${col}`}>{n} {lvl.replace(' ','·')}</span>
                })}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {analysis?.fromCache === false && <span className="text-[10px] text-gray-400">fresh</span>}
            {analysis?.fromCache === true  && <span className="text-[10px] text-gray-400">cached</span>}
            <button onClick={e => { e.stopPropagation(); loadAnalysis(true) }} disabled={analysisLoading}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-indigo-600 border border-gray-200 px-2 py-1 rounded-lg">
              <RefreshCw className={`w-3 h-3 ${analysisLoading ? 'animate-spin' : ''}`} />
            </button>
            {analysisOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </div>
        </button>

        {analysisOpen && (
          <div className="border-t border-gray-50">
            {analysisLoading && !analysis && (
              <div className="flex items-center justify-center py-10 gap-3 text-gray-400">
                <RefreshCw className="w-5 h-5 animate-spin" />
                <span className="text-sm">Running 5-factor analysis…</span>
              </div>
            )}

            {/* Portfolio improvement plan */}
            {analysis?.portfolioInsights && (
              <div className="px-5 py-4 bg-indigo-50/60 border-b border-indigo-100/50">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                  <div className="bg-white rounded-xl border border-red-100 px-4 py-3">
                    <p className="text-[10px] font-bold text-red-500 uppercase tracking-wide mb-1">Top Risk</p>
                    <p className="text-xs text-gray-700 leading-snug">{analysis.portfolioInsights.topRisk}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-emerald-100 px-4 py-3">
                    <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wide mb-1">Top Strength</p>
                    <p className="text-xs text-gray-700 leading-snug">{analysis.portfolioInsights.topStrength}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-amber-100 px-4 py-3">
                    <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wide mb-1">Tail Risk (−15%)</p>
                    <p className="text-xs text-gray-700 leading-snug">{analysis.portfolioInsights.tailRisk}</p>
                  </div>
                </div>
                {analysis.portfolioInsights.improvementPlan?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-2">Improvement Roadmap</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {analysis.portfolioInsights.improvementPlan.map((p, i) => {
                        const priorityColor = p.priority === 'HIGH' ? 'border-l-red-400 bg-red-50/50' : p.priority === 'MEDIUM' ? 'border-l-amber-400 bg-amber-50/50' : 'border-l-gray-300 bg-gray-50'
                        return (
                          <div key={i} className={`border-l-4 rounded-r-lg px-3 py-2 ${priorityColor}`}>
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <span className={`text-[9px] font-black px-1.5 py-0.5 rounded ${p.priority === 'HIGH' ? 'bg-red-100 text-red-700' : p.priority === 'MEDIUM' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>{p.priority}</span>
                              <span className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide">{p.category?.replace(/_/g,' ')}</span>
                            </div>
                            <p className="text-xs text-gray-700 font-medium">{p.action}</p>
                            <p className="text-[10px] text-gray-400 mt-0.5">{p.impact}</p>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Per-holding ranked cards */}
            <div className="divide-y divide-gray-50">
              {(analysis?.rankings || []).map((h) => {
                const isOpen    = expandedRanks[h.symbol]
                const hl        = hlevel(h.healthScore)
                const verdictColor = h.verdict === 'HOLD'   ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                                  : h.verdict === 'WATCH'   ? 'bg-amber-100 text-amber-800 border-amber-200'
                                  : h.verdict === 'REDUCE'  ? 'bg-orange-100 text-orange-800 border-orange-200'
                                  : 'bg-red-100 text-red-800 border-red-200'
                const sentScore = h.factors?.sentimentScore?.score ?? 50
                const sentColor = sentScore >= 60 ? 'text-emerald-600' : sentScore <= 40 ? 'text-red-500' : 'text-gray-500'
                const sentLabel = sentScore >= 70 ? 'Very Bullish' : sentScore >= 60 ? 'Bullish' : sentScore >= 45 ? 'Neutral' : sentScore >= 30 ? 'Bearish' : 'Very Bearish'

                return (
                  <div key={h.symbol} className="px-5">
                    {/* Row header — always visible */}
                    <button
                      onClick={() => setExpandedRanks(e => ({ ...e, [h.symbol]: !e[h.symbol] }))}
                      className="w-full py-3 flex items-center justify-between gap-3 hover:bg-gray-50/50 -mx-5 px-5 transition-colors">
                      <div className="flex items-center gap-3 min-w-0">
                        {/* Rank badge */}
                        <span className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-[11px] font-black text-gray-500 flex-shrink-0">
                          {h.rank}
                        </span>
                        {/* Symbol + risk level */}
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-900">{h.symbol}</span>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${hl.bg} ${hl.text} ${hl.border}`}>
                              {h.riskLevel}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-[10px] text-gray-400 mt-0.5">
                            <span>Score <span className={`font-bold ${hl.text}`}>{h.healthScore}</span></span>
                            <span>P&L <span className={`font-semibold ${(h.pnlPct||0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{(h.pnlPct||0) >= 0 ? '+' : ''}{(h.pnlPct||0).toFixed(1)}%</span></span>
                            <span>{fmt(h.portfolioPct, 1)}% book</span>
                            <span className={`font-medium ${sentColor}`}>{sentLabel}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`text-xs px-2.5 py-1 rounded-lg font-bold border ${verdictColor}`}>{h.verdict}</span>
                        {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </div>
                    </button>

                    {/* Expanded detail */}
                    {isOpen && (
                      <div className="pb-5 space-y-4">

                        {/* Factor scores */}
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                          {Object.entries(h.factors || {}).map(([key, f]) => {
                            const fhl = hlevel(f.score)
                            const labels = { returnQuality: 'Return', momentum: 'Momentum', positioning: 'Positioning', riskAdjusted: 'Risk-Adj', sentimentScore: 'Sentiment' }
                            return (
                              <div key={key} className={`rounded-xl border p-3 ${fhl.bg} ${fhl.border}`}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className={`text-[10px] font-semibold ${fhl.text}`}>{labels[key] || key}</span>
                                  <span className="text-[9px] text-gray-400">{f.weight}</span>
                                </div>
                                <div className={`text-xl font-black tabular-nums ${fhl.text}`}>{f.score}</div>
                                <div className="mt-1.5 h-1 bg-white/50 rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${fhl.bar}`} style={{ width: `${f.score}%` }} />
                                </div>
                                <p className="text-[9px] text-gray-500 mt-1.5 leading-tight">{f.detail}</p>
                              </div>
                            )
                          })}
                        </div>

                        {/* Strengths + Risks */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {h.strengths?.length > 0 && (
                            <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 space-y-1.5">
                              <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wide flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" /> Strengths
                              </p>
                              {h.strengths.map((s, i) => (
                                <p key={i} className="text-xs text-emerald-800 flex items-start gap-1.5 leading-snug">
                                  <span className="text-emerald-400 mt-0.5 flex-shrink-0">▶</span>{s}
                                </p>
                              ))}
                            </div>
                          )}
                          {h.risks?.length > 0 && (
                            <div className="bg-red-50 border border-red-100 rounded-xl p-3 space-y-1.5">
                              <p className="text-[10px] font-bold text-red-700 uppercase tracking-wide flex items-center gap-1">
                                <XCircle className="w-3 h-3" /> Risks
                              </p>
                              {h.risks.map((r, i) => (
                                <p key={i} className="text-xs text-red-800 flex items-start gap-1.5 leading-snug">
                                  <span className="text-red-400 mt-0.5 flex-shrink-0">▶</span>{r}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Action recommendation */}
                        {h.action && (
                          <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 flex gap-3">
                            <Info className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1">Recommended Action</p>
                              <p className="text-xs text-gray-700 leading-relaxed">{h.action}</p>
                              <span className={`inline-block mt-1.5 text-[10px] px-2 py-0.5 rounded font-bold border ${verdictColor}`}>
                                Confidence: {h.verdictConfidence} · Data: {h.dataConfidence}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* News insights with source links */}
                        {h.newsInsights?.length > 0 && (
                          <div>
                            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-2">
                              News Analysis — {h.factors?.sentimentScore?.detail || ''}
                            </p>
                            <div className="space-y-2">
                              {h.newsInsights.map((n, i) => {
                                const sentBg = n.sentiment === 'bearish' ? 'border-l-red-400 bg-red-50/40'
                                             : n.sentiment === 'bullish' ? 'border-l-emerald-400 bg-emerald-50/40'
                                             : 'border-l-gray-300 bg-gray-50'
                                return (
                                  <div key={i} className={`border-l-4 rounded-r-lg px-3 py-2 ${sentBg}`}>
                                    <div className="flex items-start justify-between gap-2">
                                      <a href={n.url} target="_blank" rel="noreferrer"
                                        className="text-xs text-gray-700 hover:text-indigo-700 font-medium leading-snug flex-1">
                                        {n.title}
                                      </a>
                                      <div className="flex-shrink-0 flex flex-col items-end gap-1">
                                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                                          n.sentiment === 'bearish' ? 'bg-red-100 text-red-700'
                                        : n.sentiment === 'bullish' ? 'bg-emerald-100 text-emerald-700'
                                        : 'bg-gray-100 text-gray-500'}`}>
                                          {n.sentiment} {n.score != null ? `· ${n.score}` : ''}
                                        </span>
                                        <a href={n.url} target="_blank" rel="noreferrer"
                                          className="text-[10px] text-indigo-500 hover:underline flex items-center gap-0.5">
                                          <ExternalLink className="w-2.5 h-2.5" />{n.source}
                                        </a>
                                      </div>
                                    </div>
                                    {n.matchedKeywords?.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-1.5">
                                        {n.matchedKeywords.slice(0, 5).map((kw, j) => (
                                          <span key={j} className="text-[9px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 font-medium">{kw}</span>
                                        ))}
                                      </div>
                                    )}
                                    <p className="text-[10px] text-gray-400 mt-1">{n.publishedAt ? new Date(n.publishedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>

      {/* ════════════════════════════════════════════════════════
          ALERTS
      ════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <AlertFeed onReviewOrder={reviewOrder} refreshKey={feedKey} />
        <AlertRulesPanel />
      </div>

      {/* ════════════════════════════════════════════════════════
          NEWS (collapsible)
      ════════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <button
          onClick={() => setNewsOpen(v => !v)}
          className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-2">
            <Newspaper className="w-4 h-4 text-indigo-500" />
            <span className="font-semibold text-gray-900">Market News</span>
            <span className="text-xs text-gray-400">via Zerodha Pulse</span>
            {pulse.total > 0 && (
              <span className="text-[10px] bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-bold">{pulse.total}</span>
            )}
            {(pulse.held?.length || 0) > 0 && (
              <span className="flex items-center gap-0.5 text-xs text-amber-600 font-medium">
                <AlertTriangle className="w-3 h-3" />{pulse.held.length} holdings
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer" onClick={e => e.stopPropagation()}>
              <input type="checkbox" checked={autoNews} onChange={e => setAutoNews(e.target.checked)} className="rounded" />
              Auto 5m
            </label>
            <button onClick={e => { e.stopPropagation(); refreshNews(false) }} disabled={refreshingNews}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 px-2.5 py-1 rounded-lg hover:bg-gray-50">
              <RefreshCw className={`w-3 h-3 ${refreshingNews ? 'animate-spin' : ''}`} />
            </button>
            {newsOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </div>
        </button>

        {newsOpen && (
          <div className="px-5 pb-5 space-y-5 border-t border-gray-50">
            {/* Holdings news — grid */}
            {pulse.held?.length > 0 && (
              <div className="pt-4">
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-3">
                  Your Holdings ({pulse.held.length})
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {pulse.held.map(({ symbol, items }) => (
                    <div key={symbol} className="border border-amber-100 bg-amber-50/40 rounded-xl p-3">
                      <p className="font-black text-gray-800 text-xs mb-2 flex items-center gap-1">
                        <Zap className="w-3 h-3 text-amber-500" />{symbol}
                      </p>
                      <ul className="space-y-1.5">
                        {items.slice(0, 2).map((item, i) => (
                          <li key={i}>
                            <a href={item.url} target="_blank" rel="noreferrer"
                              className="text-[11px] text-gray-600 hover:text-indigo-700 flex items-start gap-1.5 leading-snug">
                              <ExternalLink className="w-2.5 h-2.5 mt-0.5 shrink-0 text-gray-300" />
                              {item.title}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* General news */}
            {pulse.general?.length > 0 && (
              <div className="pt-2">
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-3">General Market</p>
                <ul className="divide-y divide-gray-50">
                  {pulse.general.slice(0, 10).map((item, i) => (
                    <li key={i} className="py-2 flex items-start gap-2">
                      <a href={item.url} target="_blank" rel="noreferrer"
                        className="text-xs text-gray-600 hover:text-indigo-700 flex items-start gap-1.5 flex-1 min-w-0">
                        <ExternalLink className="w-3 h-3 mt-0.5 shrink-0 text-gray-300" />
                        {item.title}
                      </a>
                      <span className="text-[10px] text-gray-400 whitespace-nowrap shrink-0">{item.source?.slice(0, 14)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!pulse.held?.length && !pulse.general?.length && (
              <p className="pt-4 text-center text-sm text-gray-400">Click Refresh to fetch from Zerodha Pulse</p>
            )}
          </div>
        )}
      </div>

      {/* ── Order modal ── */}
      <OrderModal
        isOpen={orderModal.isOpen}
        onClose={() => setOrderModal({ isOpen: false, stock: null, suggested: null, eventId: null })}
        stock={orderModal.stock}
        orderType="SELL"
        ltp={orderModal.suggested?.price || 0}
        initialQuantity={orderModal.suggested?.quantity}
        initialPrice={orderModal.suggested?.price}
        initialPositionType="LONG"
      />
    </div>
  )
}

export default Health
